// ============================================================================
// Ideas Page — AI-generated content ideas for CCM
// ============================================================================

import { useState, useCallback, useEffect } from 'react';
import {
  Lightbulb,
  Settings,
  Search,
  Play,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  Sparkles,
  ExternalLink,
  Download,
  Archive,
  CheckCircle,
  Trash2,
  Tag,
  Film,
  Globe,
} from 'lucide-react';
import { format, isToday } from 'date-fns';
import { useIdeas, useIdeaConfig, useGenerationStatus } from '../hooks/useIdeas';
import { api } from '../services/api';
import type { Idea, IdeaCategory, IdeaConfig } from '../types';
import { IDEA_CATEGORY_INFO } from '../types';

const ALL_CATEGORIES: IdeaCategory[] = [
  'market-trends',
  'first-time-buyers',
  'refinancing',
  'industry-news',
  'tips-tricks',
  'behind-scenes',
  'client-stories',
];

const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'title:asc', label: 'A → Z' },
  { value: 'title:desc', label: 'Z → A' },
];

const FREQUENCY_OPTIONS = [
  { value: 1, label: 'Every 1 hour' },
  { value: 2, label: 'Every 2 hours' },
  { value: 4, label: 'Every 4 hours' },
  { value: 6, label: 'Every 6 hours' },
  { value: 12, label: 'Every 12 hours' },
  { value: 24, label: 'Every 24 hours' },
];

export function IdeasPage() {
  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sortValue, setSortValue] = useState('createdAt:desc');
  const [statusFilter] = useState('active');

  // UI state
  const [focusInput, setFocusInput] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [generating, setGenerating] = useState(false);

  // Parse sort
  const [sortBy, sortOrder] = sortValue.split(':');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Data
  const { ideas, loading, refetch } = useIdeas({
    category: selectedCategory || undefined,
    status: statusFilter,
    search: debouncedSearch || undefined,
    sortBy,
    sortOrder,
  });

  const { status: genStatus, startPolling, fetchStatus } = useGenerationStatus();

  // Start polling on mount to check initial status
  useEffect(() => {
    fetchStatus().then((s) => {
      if (s?.running) startPolling();
    });
  }, []);

  // Refetch ideas when generation completes
  useEffect(() => {
    if (genStatus && !genStatus.running && genStatus.completedAt) {
      refetch();
    }
  }, [genStatus?.running, genStatus?.completedAt]);

  const handleGenerate = useCallback(async () => {
    try {
      setGenerating(true);
      await api.triggerIdeaGeneration({
        focus: focusInput || undefined,
        count: 1,
      });
      startPolling();
      setFocusInput('');
    } catch (err) {
      console.error('Failed to trigger generation:', err);
    } finally {
      setGenerating(false);
    }
  }, [focusInput, startPolling]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Lightbulb className="h-7 w-7 text-yellow-500" />
            New Ideas
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            AI-generated content ideas for Cream City Mortgage
          </p>
        </div>
        <button
          onClick={() => setShowConfigModal(true)}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Settings className="h-4 w-4" />
          Config
        </button>
      </div>

      {/* Generation Controls */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        {/* Progress bar when running */}
        {genStatus?.running && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-brand-green font-medium">
                <Sparkles className="h-4 w-4 animate-pulse" />
                {genStatus.progress || 'Generating...'}
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-brand-green animate-pulse" style={{ width: '60%' }} />
            </div>
          </div>
        )}

        {/* Focus input + generate button */}
        <div className="flex gap-3">
          <input
            type="text"
            value={focusInput}
            onChange={(e) => setFocusInput(e.target.value)}
            placeholder="Optional: focus on a topic (e.g., 'spring homebuying tips')"
            className="flex-1 rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
          />
          <button
            onClick={handleGenerate}
            disabled={generating || genStatus?.running === true}
            className="flex items-center gap-2 rounded-lg bg-brand-green px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" />
            Generate Now
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search ideas..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
          />
        </div>

        {/* Category filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
        >
          <option value="">All Categories</option>
          {ALL_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {IDEA_CATEGORY_INFO[cat].label}
            </option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={sortValue}
          onChange={(e) => setSortValue(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Ideas Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green" />
        </div>
      ) : ideas.length === 0 ? (
        <div className="text-center py-16">
          <Lightbulb className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No ideas yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Click "Generate Now" to create your first AI-powered content idea.
          </p>
        </div>
      ) : (
        (() => {
          const todayIdeas = ideas.filter((idea) => isToday(new Date(idea.createdAt)));
          const previousIdeas = ideas.filter((idea) => !isToday(new Date(idea.createdAt)));
          return (
            <div className="space-y-8">
              {/* Today section */}
              {todayIdeas.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Today</h2>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {todayIdeas.map((idea) => (
                      <IdeaCard key={idea.ideaId} idea={idea} onClick={() => setSelectedIdea(idea)} />
                    ))}
                  </div>
                </div>
              )}
              {/* Previous section */}
              {previousIdeas.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Previous</h2>
                  <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
                    {previousIdeas.map((idea) => (
                      <IdeaCompactRow key={idea.ideaId} idea={idea} onClick={() => setSelectedIdea(idea)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()
      )}

      {/* Detail Modal */}
      {selectedIdea && (
        <IdeaDetailModal
          idea={selectedIdea}
          onClose={() => setSelectedIdea(null)}
          onUpdate={() => {
            refetch();
            setSelectedIdea(null);
          }}
        />
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <ConfigModal onClose={() => setShowConfigModal(false)} />
      )}
    </div>
  );
}

// ── Idea Card Component ─────────────────────────────────────────────────

function IdeaCard({ idea, onClick }: { idea: Idea; onClick: () => void }) {
  const categoryInfo = IDEA_CATEGORY_INFO[idea.category] || {
    label: idea.category,
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  };

  const firstImage = idea.imageDownloadUrls?.[0];

  return (
    <button
      onClick={onClick}
      className="text-left rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-md hover:border-gray-300 transition-all"
    >
      {/* Thumbnail */}
      {firstImage ? (
        <div className="aspect-video w-full bg-gray-100 overflow-hidden">
          <img
            src={firstImage}
            alt={idea.title}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="aspect-video w-full bg-gradient-to-br from-brand-green/10 to-brand-blue/10 flex items-center justify-center">
          <Lightbulb className="h-10 w-10 text-brand-green/30" />
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-2">
        <h3 className="font-semibold text-gray-900 line-clamp-2 text-sm leading-snug">
          {idea.title}
        </h3>

        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${categoryInfo.bgColor} ${categoryInfo.color}`}>
            {categoryInfo.label}
          </span>
        </div>

        <p className="text-xs text-gray-500 line-clamp-2">
          {typeof idea.description === 'string'
            ? idea.description
            : idea.description && typeof idea.description === 'object'
              ? Object.values(idea.description as Record<string, unknown>).filter(Boolean).join(' — ')
              : ''}
        </p>

        <div className="flex items-center justify-between text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {format(new Date(idea.createdAt), 'MMM d, yyyy, h:mm a')}
          </span>
          {idea.imageUrls?.length > 0 && (
            <span>{idea.imageUrls.length} image{idea.imageUrls.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Idea Compact Row Component (for previous ideas) ─────────────────────

function IdeaCompactRow({ idea, onClick }: { idea: Idea; onClick: () => void }) {
  const categoryInfo = IDEA_CATEGORY_INFO[idea.category] || {
    label: idea.category,
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  };

  const firstImage = idea.imageDownloadUrls?.[0];

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
    >
      {/* Thumbnail */}
      {firstImage ? (
        <img
          src={firstImage}
          alt={idea.title}
          className="h-12 w-12 flex-shrink-0 rounded-lg object-cover bg-gray-100"
        />
      ) : (
        <div className="h-12 w-12 flex-shrink-0 rounded-lg bg-gradient-to-br from-brand-green/10 to-brand-blue/10 flex items-center justify-center">
          <Lightbulb className="h-5 w-5 text-brand-green/30" />
        </div>
      )}

      {/* Title */}
      <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">
        {idea.title}
      </span>

      {/* Category badge */}
      <span className={`flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${categoryInfo.bgColor} ${categoryInfo.color}`}>
        {categoryInfo.label}
      </span>

      {/* Date + time */}
      <span className="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap">
        {format(new Date(idea.createdAt), 'MMM d, yyyy h:mm a')}
      </span>
    </button>
  );
}

// ── Idea Detail Modal ───────────────────────────────────────────────────

function IdeaDetailModal({
  idea,
  onClose,
  onUpdate,
}: {
  idea: Idea;
  onClose: () => void;
  onUpdate: () => void;
}) {
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [fullIdea, setFullIdea] = useState<Idea>(idea);

  // Fetch full idea detail
  useEffect(() => {
    api.getIdea(idea.ideaId).then(setFullIdea).catch(console.error);
  }, [idea.ideaId]);

  const categoryInfo = IDEA_CATEGORY_INFO[fullIdea.category] || {
    label: fullIdea.category,
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  };

  const togglePlatform = (platform: string) => {
    setExpandedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  const handleAction = async (action: 'used' | 'archived' | 'delete') => {
    try {
      setActionLoading(true);
      if (action === 'delete') {
        await api.deleteIdea(fullIdea.ideaId);
      } else {
        await api.updateIdea(fullIdea.ideaId, { status: action });
      }
      onUpdate();
    } catch (err) {
      console.error('Action failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Parse description for inline references
  const renderDescription = (desc: unknown): string => {
    if (typeof desc === 'string') {
      return desc.replace(/\[(\d+)\]/g, (_match, num) => {
        return `<a href="#source-${num}" class="text-brand-blue hover:underline font-medium">[${num}]</a>`;
      });
    }
    if (desc && typeof desc === 'object') {
      // If description comes back as an object, render its values as paragraphs
      return Object.entries(desc as Record<string, unknown>)
        .filter(([, v]) => v)
        .map(([key, value]) => `<p><strong>${key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}:</strong> ${String(value)}</p>`)
        .join('');
    }
    return '';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pb-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-4xl rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6 lg:p-8 space-y-8 max-h-[90vh] overflow-y-auto">
          {/* Title + Meta */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 pr-10">{fullIdea.title}</h2>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${categoryInfo.bgColor} ${categoryInfo.color}`}>
                {categoryInfo.label}
              </span>
              {fullIdea.tags?.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(fullIdea.createdAt).toLocaleString()}
              </span>
              <span>Model: {fullIdea.generatedBy}</span>
            </div>
          </div>

          {/* Description */}
          <div className="border-t border-gray-100 pt-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Description</h3>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: renderDescription(fullIdea.description) }}
            />
          </div>

          {/* Platform Strategy */}
          {fullIdea.platformStrategy && Object.keys(fullIdea.platformStrategy).length > 0 && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                <Globe className="h-4 w-4" />
                Platform Strategy
              </h3>
              <div className="space-y-2">
                {Object.entries(fullIdea.platformStrategy).map(([platform, strategy]) => (
                  <div key={platform} className="rounded-lg border border-gray-100 bg-gray-50">
                    <button
                      onClick={() => togglePlatform(platform)}
                      className="flex w-full items-center justify-between p-3 text-left"
                    >
                      <span className="text-sm font-medium text-gray-800 capitalize">
                        {platform.replace(/_/g, ' ')}
                      </span>
                      {expandedPlatforms.has(platform) ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    {expandedPlatforms.has(platform) && (
                      <div className="border-t border-gray-100 p-3 space-y-2 text-sm text-gray-600">
                        <div>
                          <span className="font-medium text-gray-700">Approach:</span>{' '}
                          {strategy.approach}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Why it works:</span>{' '}
                          {strategy.whyItWorks}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Best format:</span>{' '}
                          {strategy.bestFormat}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filming Plan */}
          {fullIdea.filmingPlan && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                <Film className="h-4 w-4" />
                Filming Plan
              </h3>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-5 text-sm text-gray-700">
                {typeof fullIdea.filmingPlan === 'string' ? (
                  <p className="whitespace-pre-wrap">{fullIdea.filmingPlan}</p>
                ) : fullIdea.filmingPlan && typeof fullIdea.filmingPlan === 'object' ? (
                  <div className="space-y-4">
                    {fullIdea.filmingPlan.scriptOutline && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Script Outline</h5>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{fullIdea.filmingPlan.scriptOutline}</p>
                      </div>
                    )}
                    {fullIdea.filmingPlan.hostNotes && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Host Notes</h5>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{fullIdea.filmingPlan.hostNotes}</p>
                      </div>
                    )}
                    {fullIdea.filmingPlan.cameraAngles && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Camera Angles</h5>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{fullIdea.filmingPlan.cameraAngles}</p>
                      </div>
                    )}
                    {fullIdea.filmingPlan.setting && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Setting</h5>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{fullIdea.filmingPlan.setting}</p>
                      </div>
                    )}
                    {fullIdea.filmingPlan.props && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Props</h5>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{fullIdea.filmingPlan.props}</p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Sources */}
          {fullIdea.sources && fullIdea.sources.length > 0 && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Sources</h3>
              <ol className="space-y-2">
                {fullIdea.sources.map((source, idx) => (
                  <li key={idx} id={`source-${idx + 1}`} className="flex gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <span className="flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-brand-green/10 text-xs font-bold text-brand-green">
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-brand-blue hover:underline flex items-center gap-1"
                      >
                        {source.title}
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                      <p className="text-xs text-gray-500 mt-0.5">{source.snippet}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Concept Images */}
          {fullIdea.imageDownloadUrls && fullIdea.imageDownloadUrls.length > 0 && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Concept Images</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {fullIdea.imageDownloadUrls.map((url, idx) => (
                  <div key={idx} className="group relative rounded-lg overflow-hidden border border-gray-200">
                    <img src={url} alt={`Concept ${idx + 1}`} className="w-full aspect-square object-cover" />
                    <a
                      href={url}
                      download={`concept-${idx + 1}.png`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-2 right-2 rounded-lg bg-black/60 p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 border-t border-gray-100 pt-4">
            <button
              onClick={() => handleAction('used')}
              disabled={actionLoading}
              className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-100 disabled:opacity-50 transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              Mark as Used
            </button>
            <button
              onClick={() => handleAction('archived')}
              disabled={actionLoading}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors"
            >
              <Archive className="h-4 w-4" />
              Archive
            </button>
            <button
              onClick={() => handleAction('delete')}
              disabled={actionLoading}
              className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors ml-auto"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Config Modal ────────────────────────────────────────────────────────

function ConfigModal({ onClose }: { onClose: () => void }) {
  const { config, loading, updateConfig } = useIdeaConfig();
  const [localConfig, setLocalConfig] = useState<IdeaConfig | null>(null);
  const [saving, setSaving] = useState(false);

  // Models dropdown state
  const [models, setModels] = useState<{ id: string; name: string }[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState('');
  const [customOverride, setCustomOverride] = useState('');

  useEffect(() => {
    if (config) {
      setLocalConfig({ ...config });
    }
  }, [config]);

  // Fetch models when modal opens
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setModelsLoading(true);
        setModelsError(null);
        const result = await api.listModels();
        if (cancelled) return;
        setModels(result.models);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch models:', err);
        setModelsError('Failed to load models');
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Once we have both config and models, determine dropdown vs custom override
  useEffect(() => {
    if (!config || modelsLoading) return;
    const currentModel = config.model || '';
    const isInList = models.some((m) => m.id === currentModel);
    if (isInList) {
      setSelectedModel(currentModel);
      setCustomOverride('');
    } else {
      // Current model isn't in the list — put it in custom override
      setSelectedModel(models.length > 0 ? models[0]!.id : '');
      setCustomOverride(currentModel);
    }
  }, [config, models, modelsLoading]);

  const handleSave = async () => {
    if (!localConfig) return;
    try {
      setSaving(true);
      // Custom override takes precedence if non-empty
      const modelValue = customOverride.trim() || selectedModel;
      await updateConfig({ ...localConfig, model: modelValue });
      onClose();
    } catch (err) {
      console.error('Failed to save config:', err);
    } finally {
      setSaving(false);
    }
  };

  // Group models by provider for the dropdown
  const groupedModels = models.reduce<Record<string, { id: string; name: string }[]>>((acc, model) => {
    const slashIdx = model.id.indexOf('/');
    const provider = slashIdx > 0 ? model.id.substring(0, slashIdx) : 'other';
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(model);
    return acc;
  }, {});

  const sortedProviders = Object.keys(groupedModels).sort();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-lg font-bold text-gray-900 mb-4">Idea Generation Config</h2>

        {loading || !localConfig ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-green" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Enabled toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Auto-generate</p>
                <p className="text-xs text-gray-500">Automatically generate ideas on a schedule</p>
              </div>
              <button
                onClick={() =>
                  setLocalConfig({ ...localConfig, enabled: !localConfig.enabled })
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localConfig.enabled ? 'bg-brand-green' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localConfig.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select
                value={localConfig.frequencyHours}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    frequencyHours: parseInt(e.target.value, 10),
                  })
                }
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* AI Model Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">AI Model</label>
              {modelsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-green" />
                  <span className="text-sm text-gray-500">Loading models...</span>
                </div>
              ) : modelsError ? (
                <div className="space-y-2">
                  <p className="text-sm text-red-600">{modelsError}</p>
                  <input
                    type="text"
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    placeholder="Enter model ID manually"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
                  />
                </div>
              ) : (
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
                >
                  {sortedProviders.map((provider) => (
                    <optgroup key={provider} label={provider}>
                      {groupedModels[provider]!.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.id.substring(model.id.indexOf('/') + 1)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>

            {/* Custom Model Override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Model Override
                <span className="ml-1 text-xs font-normal text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={customOverride}
                onChange={(e) => setCustomOverride(e.target.value)}
                placeholder="Leave empty to use dropdown selection"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
              />
              <p className="mt-1 text-xs text-gray-400">
                Use this if you need a model not in the list above
              </p>
            </div>

            {/* Default focus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Focus Topic</label>
              <input
                type="text"
                value={localConfig.defaultFocus}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, defaultFocus: e.target.value })
                }
                placeholder="e.g., 'spring homebuying season'"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
              />
            </div>

            {/* Save */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-brand-green px-4 py-2 text-sm font-medium text-white hover:bg-brand-green/90 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
